import React, {useEffect, useState} from "react";
import { ModalType } from "common/common-models";
import { EditChargingReportConfigurationModal } from "components/ChargingReportConfigurations/modals/EditChargingReportConfigurationModal";
import { ChargingReportConfiguration, ChargingReportConfigurationFilters, ChargingReportConfigurationSortFields } from "components/ChargingReportConfigurations/models";
import { ChargingReportConfigurationsSearchBar } from "components/ChargingReportConfigurations/ChargingReportConfigurationsSearchBar";
import { ChargingReportConfigurationsTable } from "components/ChargingReportConfigurations/ChargingReportConfigurationsTable";
import { IPageResponse, SortDirection } from "common/interfaces/IPageResponse";
import { IPageRequest } from "common/interfaces/IPageRequest";
import { ChargingReportConfigurationsService } from "components/ChargingReportConfigurations/ChargingReportConfigurations.service";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { DeleteChargingReportConfigurationConfirmModal } from "components/ChargingReportConfigurations/modals/DeleteChargingReportConfigurationConfirmModal";

interface ChargingReportConfigurationsProps {
    onLoadValidatePage: () => void;
}

interface ModalState {
    configuration?: ChargingReportConfiguration;
    modalType?: ModalType;
}

const searchApi = AwesomeDebouncePromise(
    request => ChargingReportConfigurationsService.findChargingReportConfigurations(request),
    300
);

const ChargingReportConfigurations = (props : ChargingReportConfigurationsProps ) => {
    const [chargingReportConfigurations, setChargingReportConfigurations] = useState<IPageResponse<ChargingReportConfiguration>>();
    const [pageRequest, setPageRequest] = useState<IPageRequest<ChargingReportConfigurationSortFields>>({
        page: 0,
        size: 20,
        sortDirection: SortDirection.ASC,
        sortField: ChargingReportConfigurationSortFields.ID,
        searchQuery: null
    });
    const [filters, setFilters] = useState<ChargingReportConfigurationFilters>({});
    const [isRequesting, setIsRequesting] = useState(false);
    const [deleteModal, setDeleteModal] = useState<ModalState>();
    const [editModal, setEditModal] = useState<ModalState>();

    const searchChargingReportConfigurations = (request: any, isInitial: boolean) => {
            if(isInitial) setIsRequesting(true);

            searchApi(request).then((response: IPageResponse<ChargingReportConfiguration>) => {
                setChargingReportConfigurations(response)
                setIsRequesting(false)
            })
    };

    const onPageChange = (targetPage: number) => {
        const page = targetPage - 1;
        setPageRequest({
            ...pageRequest,
            page
        })

        searchChargingReportConfigurations({
            page,
            size: pageRequest.size,
            sortDirection: pageRequest.sortDirection,
            sortField: pageRequest.sortField,
            searchQuery: filters?.search
        }, false)
    };

    const handleSortClick = (sortField: ChargingReportConfigurationSortFields) => {
            let sortDirection = SortDirection.ASC;
            if (pageRequest.sortField.match(sortField)) {
                sortDirection = pageRequest.sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC;
            }

            setPageRequest({
                ...pageRequest,
                sortDirection,
                sortField
            })

            searchChargingReportConfigurations({
                page: pageRequest.page,
                size: pageRequest.size,
                searchQuery: filters?.search,
                sortDirection,
                sortField,
            }, false)
    };

    const handleFilterChange = (change: ChargingReportConfigurationFilters) => {
        setChargingReportConfigurations(undefined);
        setPageRequest({
            ...pageRequest,
            page: 0
        })
        setFilters({
            ...filters,
            ...change
        })

        searchChargingReportConfigurations({
            page: 0,
            size: pageRequest.size,
            sortDirection: pageRequest.sortDirection,
            sortField: pageRequest.sortField,
            searchQuery: change?.search
        }, false)
    };

    const openEditChargingReportConfigurationModal = (modalType: ModalType, configuration?: ChargingReportConfiguration) => {
        setEditModal({
            modalType: modalType,
            configuration
        });
      };

    const openDeleteChargingReportConfigurationModal = (configuration: ChargingReportConfiguration) => {
        setDeleteModal({
            configuration
        });
    };

    const initializeRequests = (isInitial: boolean) => {
        searchChargingReportConfigurations({
            page: 0,
            size: pageRequest.size,
            sortDirection: pageRequest.sortDirection,
            sortField: pageRequest.sortField,
            searchQuery: filters?.search
        }, isInitial);
    }

    useEffect(() => {
        props.onLoadValidatePage();
        initializeRequests(true);
          // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className="container">
            <div className="row">
                <div className="col">
                    <ChargingReportConfigurationsSearchBar
                    onFilter={handleFilterChange} 
                    filters={filters}
                    openChargingReportConfigurationModal={openEditChargingReportConfigurationModal}
                    />
                    <ChargingReportConfigurationsTable 
                    onPageChange={onPageChange}
                    onSort={handleSortClick}
                    configurations={chargingReportConfigurations}
                    sortDirection={pageRequest.sortDirection}
                    sortField={pageRequest.sortField}
                    openEditChargingReportConfigurationModal={openEditChargingReportConfigurationModal}
                    openDeleteChargingReportConfigurationModal={openDeleteChargingReportConfigurationModal}
                    isRequesting={isRequesting}
                    />
                </div>
            </div>    
            {
                editModal && (
                    <EditChargingReportConfigurationModal
                        handleModalHide={() => { setEditModal(undefined) }}
                        modalType={editModal.modalType!}
                        loadChargingReportConfigurations={() => initializeRequests(false)}
                        chargingReportConfiguration={editModal.configuration}
                    />
                )
            }
            {
                deleteModal && (
                    <DeleteChargingReportConfigurationConfirmModal
                        handleRejectClose={() => {
                            setDeleteModal(undefined)
                        }}
                        handleSubmitClose={() => {
                            setDeleteModal(undefined)
                            initializeRequests(false);
                        }}
                        chargingReportConfiguration={deleteModal.configuration!}
                    />
                )
            }
        </div>
    );
}

export default ChargingReportConfigurations;